import React, { useState } from 'react';
import './ManageProducts.css';

function ReasonModal({ action, productIds, isBulk, onSubmit, onClose, title }) {
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [customReason, setCustomReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Pre-defined template responses
  const getTemplateResponses = () => {
    if (action === 'reject') {
      return [
        'Product images are low quality or inappropriate',
        'Product description is incomplete or unclear',
        'Product pricing is not competitive',
        'Product category is incorrect',
        'Product violates platform policies',
        'Missing required product information',
        'Product title is misleading or inaccurate',
        'Product specifications are incorrect'
      ];
    } else if (action === 'request_changes') {
      return [
        'Please improve product images quality',
        'Product description needs more detail',
        'Please update pricing to be more competitive',
        'Product category needs to be corrected',
        'Please add missing product specifications',
        'Product title needs to be more descriptive',
        'Please add more product features information',
        'Product dimensions are missing'
      ];
    }
    return [];
  };

  const handleTemplateChange = (template) => {
    setSelectedTemplates(prev => 
      prev.includes(template) 
        ? prev.filter(t => t !== template)
        : [...prev, template]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const combined = [...selectedTemplates, customReason.trim()].filter(Boolean).join('; ');

    if (!combined) {
      alert('Please provide a reason for this action.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Send a simple string reason to backend to avoid nested objects in audit payload
      await onSubmit(combined);
    } catch (error) {
      console.error('Error submitting reason:', error);
      alert('Error submitting. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const getActionTitle = () => {
    switch (action) {
      case 'reject':
        return 'Reject Product' + (isBulk ? 's' : '');
      case 'request_changes':
        return 'Request Changes for Product' + (isBulk ? 's' : '');
      default:
        return 'Product Action';
    }
  };
  
  const getActionDescription = () => {
    switch (action) {
      case 'reject':
        return isBulk 
          ? `You are about to reject ${productIds.length} product(s). This action will remove them from the approval queue.`
          : 'You are about to reject this product. This action will remove it from the approval queue.';
      case 'request_changes':
        return isBulk
          ? `You are about to request changes for ${productIds.length} product(s). The vendor will be notified to make the requested changes.`
          : 'You are about to request changes for this product. The vendor will be notified to make the requested changes.';
      default:
        return 'Please provide a reason for this action.';
    }
  };
  
  const getPlaceholderText = () => {
    switch (action) {
      case 'reject':
        return 'Please explain why this product is being rejected...';
      case 'request_changes':
        return 'Please describe the changes needed for this product...';
      default:
        return 'Please provide a reason...';
    }
  };
  
  return (
    <div className="modalOverlay">
      <div className="modalContent reasonModal" style={{ width: '980px', maxWidth: '980px', maxHeight: '90vh' }}>
        <div className="modalHeader">
          <h3>{title || getActionTitle()}</h3>
          <button 
            className="modalClose"
            onClick={onClose}
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>
        
        <div className="modalBody">
          <p className="actionDescription">
            {getActionDescription()}
          </p>
          
          {isBulk && (
            <div className="productIdsInfo">
              <strong>Product IDs:</strong>
              <div className="productIdsList">
                {productIds.map((id, index) => (
                  <span key={id} className="productIdTag">
                    {id}
                    {index < productIds.length - 1 && ', '}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            {/* Template Responses */}
            <div className="formGroup">
              <label>
                Select {action === 'request_changes' ? 'Issues' : 'Reasons'} (Optional)
              </label>
              <div className="templateResponses">
                {getTemplateResponses().map((template, index) => (
                  <label key={index} className="templateResponseItem">
                    <input
                      type="checkbox"
                      checked={selectedTemplates.includes(template)}
                      onChange={() => handleTemplateChange(template)}
                      disabled={isSubmitting}
                    />
                    <span className="templateText">{template}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Custom Reason */}
            <div className="formGroup">
              <label htmlFor="customReason">
                Additional Details {selectedTemplates.length > 0 ? '(Optional)' : '*'}
              </label>
              <textarea
                id="customReason"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder={getPlaceholderText()}
                rows="2"
                required={selectedTemplates.length === 0}
                disabled={isSubmitting}
                className="reasonTextarea"
              />
              <div className="characterCount">
                {customReason.length}/500 characters
              </div>
            </div>
          </form>
        </div>
        
        <div className="modalFooter">
          <button 
            className="modalButton secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
            <button 
              className={`modalButton ${action === 'reject' ? 'danger' : 'primary'}`}
              onClick={handleSubmit}
              disabled={(!selectedTemplates.length && !customReason.trim()) || isSubmitting}
            >
              {isSubmitting ? 'Processing...' : `Confirm ${action === 'reject' ? 'Rejection' : 'Request'}`}
            </button>
        </div>
      </div>
    </div>
  );
}

export default ReasonModal;
